<template>
    <div ref="componentRef" class="autocomplete">
        <div
            ref="excludeRef"
            class="autocomplete-input"
            :class="{
                'autocomplete-input--label': label,
                focus: focusState || showSuggestions || query,
            }"
        >
            <label v-if="label" class="autocomplete-label">{{ label }}</label>
            <input
                v-model="query"
                type="text"
                :autocomplete="optionAutocomplete"
                :autocorrect="optionAutocorrect"
                :autocapitalize="optionAutocapitalize"
                :class="[...classes]"
                :placeholder="label ? '' : placeholder"
                @keyup="handleChangeValue"
                @focus="handleFocus"
                @blur="handleBlur"
            />
            <SpinnerSvg v-if="process" class="autocomplete-spinner" />
        </div>
        <Suggestions
            v-if="canShowSuggestions"
            :suggestions="suggestions"
            :query="query"
            @handle-set-suggestion="setSuggestion"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from 'vue';
    import { debounce } from 'lodash';
    import Suggestions from './Suggestions.vue';
    import useClickOutside from 'vueCommon/composables/useClickOutside';
    import SpinnerSvg from 'vueCommon/ui/SpinnerSvg.vue';

    const emit = defineEmits([
        'handleLazyKeyup',
        'handleFocus',
        'handleBlur',
        'handlePaste',
        'handleSetSuggestion',
        'handleChange',
    ]);

    const props = defineProps({
        suggestions: {
            type: Array,
            default: () => {
                return [];
            },
        },
        classes: {
            type: Array,
            default: () => {
                return ['form-control'];
            },
        },
        optionAutocomplete: {
            default: 'off',
            type: String,
        },
        optionAutocorrect: {
            default: 'off',
            type: String,
        },
        optionAutocapitalize: {
            default: 'off',
            type: String,
        },
        timeoutDebounce: {
            default: 1000,
            type: Number,
        },
        minLength: {
            default: 1,
            type: Number,
        },
        process: {
            default: false,
            type: Boolean,
        },
        placeholder: {
            type: String,
            default: '',
        },
        defaultValue: {
            default: '',
            type: String,
        },
        label: {
            default: '',
            type: String,
        },
    });

    const query = ref<String>('');
    const showSuggestions = ref<Boolean>(false);
    const focusState = ref<Boolean>(false);
    const componentRef = ref();
    const excludeRef = ref();

    const canShowSuggestions = computed(() => {
        return props.suggestions.length > 0 && showSuggestions.value;
    });

    watch(
        () => props.defaultValue,
        (newDefaultValue, oldDefaultValue) => {
            if (newDefaultValue && !oldDefaultValue && !query.value) {
                query.value = newDefaultValue;
            }

            if (
                newDefaultValue &&
                oldDefaultValue &&
                newDefaultValue !== oldDefaultValue
            ) {
                query.value = newDefaultValue;
            }

            /**
             * @doc удаление из родительского компонента
             */
            if (!newDefaultValue && oldDefaultValue) {
                query.value = '';
            }
        }
    );

    useClickOutside(
        componentRef,
        () => {
            showSuggestions.value = false;
        },
        excludeRef
    );

    const handleFocus = (e) => {
        focusState.value = true;
        emit('handleFocus', e);
    };

    const handleBlur = (e) => {
        focusState.value = false;
        /**
         * @doc Нельзя сбивать новым запросом уже выпадший список через событие Blur
         * иначе начинаются неприятные эффекты в связке с handleChange
         */
        if (showSuggestions.value) {
            return;
        }

        emit('handleBlur', e);
        lazyAutocomplete(e.target.value);
    };

    const handleChangeValue = (e) => {
        emit('handleChange', e.target.value);
        lazyAutocomplete(e.target.value);
    };

    const setSuggestion = (suggestion) => {
        emit('handleSetSuggestion', suggestion);
        showSuggestions.value = false;
        query.value = suggestion.mainValue;
    };

    const lazyAutocomplete = debounce((value) => {
        if (props.minLength > value.length) {
            return;
        }

        showSuggestions.value = true;
        emit('handleLazyKeyup', value);
    }, props.timeoutDebounce);
</script>

<style>
    .autocomplete-input {
        position: relative;
    }

    .autocomplete-input--label {
        align-items: center;
        display: flex;
        flex-direction: row;
        justify-content: center;
        position: relative;
    }

    .autocomplete-input--label .form-control {
        background: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 8px;
        color: #333;
        font-size: 14px;
        height: 64px;
        line-height: 22px;
        outline: none;
        padding: 14px 12px 11px;
        position: relative;
        transition: 0.2s;
        width: 100%;
    }

    .autocomplete-input--label label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 95%;
        color: #8c8c8c;
        font-size: 14px;
        pointer-events: none;
        position: absolute;
        z-index: 10;
        cursor: text;
        left: 12px;
        top: 22px;
        transition: all 0.15s ease-in-out;
        margin-bottom: 0;
        line-height: 1.4;
    }

    .autocomplete-input--label.focus label {
        top: 12px;
    }

    .autocomplete-spinner {
        position: absolute;
        right: 10px;
        top: 50%;
        margin-top: -12px;
    }
</style>
